import type { RefObject, SubmitEventHandler } from 'react';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type {
  ContentTypeDefinition,
  UserRole,
  WithOptionalInternalProps,
} from '../../lib/flotiq-client/types/api-response';

import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  setFormSubmitErrors,
  showValidationErrorToast,
} from '../../form-hooks/lib/helpers';
import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Tooltip from '../../components/Tooltip/Tooltip';

import useSelectedSpace from '../../hooks/useSelectedSpace';

import { QuestionMarkCircleIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';
import { getInitialValues } from './helpers/initialValues';
import { transformValues } from './helpers/submitTransform';

export type UserRoleFormSubmitFn = (
  values: ReturnType<typeof transformValues>,
) => Promise<
  [
    WithOptionalInternalProps<UserRole> | undefined,
    Record<string, string | string[]>,
    boolean,
  ]
>;

interface UserRoleFormProps {
  /**
   * User role item
   */
  role?: UserRole | null;
  /**
   * On save callback
   */
  onSave: UserRoleFormSubmitFn;
  /**
   * Content types data
   */
  contentTypes?: ContentTypeDefinition[];
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * If should navigate to grid on save
   */
  navigateOnSave?: RefObject<boolean>;
  /**
   * If role is duplicating
   */
  duplicate?: boolean;
  /**
   * Set form state
   */
  setFormState?: SetFormApiFn;
  /**
   * Test id for form
   */
  testId?: string;
}

const UserRoleForm = ({
  role,
  onSave,
  contentTypes = EMPTY_ARRAY,
  disabled = false,
  navigateOnSave,
  duplicate = false,
  setFormState,
  testId = '',
}: UserRoleFormProps) => {
  const { t } = useTranslation();
  const { buildUrlWithSpace } = useSelectedSpace();
  const { navigateGrid } = useGridNavigate(
    'user-roles',
    buildUrlWithSpace('user-roles'),
  );

  const ctdsOptions = useMemo(
    () =>
      contentTypes.map((ctd) => ({
        value: ctd.id,
        label: ctd.label,
        metaDefinition: ctd.metaDefinition,
        schemaDefinition: ctd.schemaDefinition,
        searchString: ctd.name + ' ' + ctd.label,
      })),
    [contentTypes],
  );

  const permissionSchemaZod = useMemo(
    () =>
      z.array(
        z.object({
          id: z.string().optional(),
          type: z.enum(['CO', 'CTD']),
          canRead: z.boolean(),
          canCreate: z.boolean(),
          canUpdate: z.boolean(),
          canDelete: z.boolean(),
          attributesPermission: z
            .object({
              canRead: z.string().array().optional(),
              canCreate: z.string().array().optional(),
              canUpdate: z.string().array().optional(),
            })
            .optional(),
          contentTypeDefinition: z.object({
            id: z
              .string()
              .nullable()
              .refine((value) => {
                if (!value) return true;
                return ctdsOptions?.findIndex((el) => el.value === value) >= 0;
              }, t('ApiKeys.ErrorSelectedNoLongerValid')),
          }),
        }),
      ),
    [ctdsOptions, t],
  );

  const validationSchemaZod = useMemo(
    () =>
      z
        .object({
          name: z.string().min(1, t('Form.FormErrorNotBlank')),
          description: z.string().min(1, t('Form.FormErrorNotBlank')),
          ctd_permissions: permissionSchemaZod,
          co_permissions: permissionSchemaZod,
        })
        .superRefine((data, ctx) => {
          if (
            data.ctd_permissions.length === 0 &&
            data.co_permissions.length === 0
          ) {
            ctx.addIssue({
              code: 'custom',
              message: t('UserRoles.PermissionsMinLength'),
              path: ['permissions'],
            });
          }
        }),
    [permissionSchemaZod, t],
  );

  const ruleColumn = useMemo(() => {
    return ['Read', 'Create', 'Update', 'Delete'].map((perm) => ({
      id: `can${perm}`,
      name: `can${perm}`,
      label: t(`Global.Permission.${perm}`),
      isFieldPermission: perm !== 'Delete',
    }));
  }, [t]);

  const initialValues = useMemo(
    () => getInitialValues(role, ruleColumn, contentTypes, duplicate),
    [contentTypes, duplicate, role, ruleColumn],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      const [newValues, errors, hasErrors] = await onSave(
        transformValues(value),
      );

      if (!hasErrors) {
        formApi.reset(
          getInitialValues(newValues, ruleColumn, contentTypes, false),
          { keepDefaultValues: true },
        );

        if (navigateOnSave?.current) {
          navigateGrid();
        }
      } else {
        const filteredErrors = setFormSubmitErrors(formApi, errors);
        showValidationErrorToast(filteredErrors);
      }
    },
    validators: {
      onChange: validationSchemaZod,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        className="max-w-4xl py-4 px-5"
        id="role-form"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <div className="flex flex-col gap-4 md:gap-6 w-full">
          <form.AppField name="name">
            {(field) => (
              <field.Input
                label={t('Global.Name')}
                disabled={disabled}
                required
                {...getTestProps(testId, 'name', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.Textarea
                label={t('Global.Description')}
                disabled={disabled}
                required
                {...getTestProps(testId, 'description', 'testId')}
              />
            )}
          </form.AppField>
        </div>

        <form.Subscribe
          selector={({ errors, fieldMeta }) => ({
            errors: errors?.[0]?.permissions,
            isTouched:
              fieldMeta?.ctd_permissions?.isTouched ||
              fieldMeta?.co_permissions?.isTouched,
          })}
        >
          {({ isTouched, errors }) =>
            isTouched && errors?.length > 0 ? (
              <div
                className="text-red text-sm mt-10 leading-none text-left"
                {...getTestProps(testId, 'permissions-error')}
              >
                {errors
                  .map((error) =>
                    typeof error === 'string' ? error : error.message,
                  )
                  .join(', ')}
              </div>
            ) : null
          }
        </form.Subscribe>

        <div className="flex mt-10 mb-3 items-center">
          <h2 className="text-3xl leading-none dark:text-white">
            {t('UserRoles.ContentTypeDefinitionRules')}
          </h2>

          <Tooltip
            tooltip={
              <Trans
                i18nKey="UserRoles.ContentTypeDefinitionRulesTooltip"
                components={{
                  1: <ul className="list-disc ml-4 mt-1" />,
                  2: <li />,
                }}
              />
            }
            phoneTooltipPlacement="topRight"
            additionalClasses="px-2"
          >
            <QuestionMarkCircleIcon className="text-slate-400 min-w-4 w-4" />
          </Tooltip>
        </div>

        <form.AppField name={'ctd_permissions'} mode="array">
          {(field) => (
            <field.PermissionsField
              ruleColumn={ruleColumn}
              ctdsOptions={ctdsOptions}
              disabled={disabled}
              type="CTD"
              ctdPlaceholder={t('UserRoles.All')}
              {...getTestProps(testId, 'ctd-permissions', 'testId')}
            />
          )}
        </form.AppField>

        <div className="flex mt-10 mb-3 items-center">
          <h2 className="text-3xl leading-none dark:text-white">
            {t('UserRoles.ContentObjectRules')}
          </h2>

          <Tooltip
            tooltip={
              <Trans
                i18nKey="UserRoles.ContentObjectRulesTooltip"
                components={{
                  1: <ul className="list-disc ml-4 my-1" />,
                  2: <li />,
                }}
              />
            }
            phoneTooltipPlacement="topRight"
            additionalClasses="px-2"
          >
            <QuestionMarkCircleIcon className="text-slate-400 min-w-4 w-4" />
          </Tooltip>
        </div>

        <form.AppField name={'co_permissions'} mode="array">
          {(field) => (
            <field.PermissionsField
              ruleColumn={ruleColumn}
              ctdsOptions={ctdsOptions}
              disabled={disabled}
              type="CO"
              ctdPlaceholder={t('UserRoles.All')}
              {...getTestProps(testId, 'co-permissions', 'testId')}
            />
          )}
        </form.AppField>

        <form.DirtyHandler />
        <form.ValidationToastHandler />
        <form.FormStateHandler setFormState={setFormState} />
      </form>
    </form.AppForm>
  );
};

export default UserRoleForm;
